import { useEffect, useRef } from "react";

const MODES = {
  warm: ["#FFD9A0", "#FFE9C4", "#FFC978"],
  cool: ["#DCEAFF", "#FFFFFF", "#BFD9FF"],
  festive: ["#EF4444", "#10B981", "#FFD700"],
  blue: ["#60A5FA", "#3B82F6", "#93C5FD"],
  gold: ["#F3E5AB", "#D4AF37", "#FFF7DC"],
};

export default function HolidayCanvas({ mode = "warm", className = "" }) {
  const ref = useRef(null);
  const modeRef = useRef(mode);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let w = 0;
    let h = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const snow = Array.from({ length: 110 }, () => ({
      x: Math.random(),
      y: Math.random(),
      r: 0.8 + Math.random() * 2.2,
      s: 0.15 + Math.random() * 0.45,
      o: 0.25 + Math.random() * 0.55,
      drift: Math.random() * Math.PI * 2,
    }));

    const strings = [
      { y: 0.10, sag: 46, bulbs: 16 },
      { y: 0.20, sag: 60, bulbs: 14 },
      { y: 0.31, sag: 42, bulbs: 18 },
    ];
    const bulbPhases = strings.map((s) =>
      Array.from({ length: s.bulbs }, () => Math.random() * Math.PI * 2)
    );

    const t0 = performance.now();

    const draw = (now) => {
      const t = (now - t0) / 1000;
      ctx.clearRect(0, 0, w, h);
      const colors = MODES[modeRef.current] || MODES.warm;

      strings.forEach((s, si) => {
        const y0 = h * s.y;
        const sag = s.sag * (w / 1400 > 1 ? 1 : w / 1400);
        ctx.strokeStyle = "rgba(255,255,255,0.10)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        for (let x = 0; x <= w; x += 12) {
          const p = x / w;
          const y = y0 + sag * (1 - Math.pow(2 * p - 1, 2));
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();

        for (let i = 0; i < s.bulbs; i++) {
          const p = (i + 0.5) / s.bulbs;
          const x = p * w;
          const y = y0 + sag * (1 - Math.pow(2 * p - 1, 2)) + 4;
          const tw = 0.45 + 0.55 * (0.5 + 0.5 * Math.sin(t * 2.2 + bulbPhases[si][i]));
          const color = colors[i % colors.length];
          const g = ctx.createRadialGradient(x, y, 0, x, y, 14 * tw);
          g.addColorStop(0, color);
          g.addColorStop(1, "transparent");
          ctx.globalAlpha = 0.5 * tw;
          ctx.fillStyle = g;
          ctx.beginPath();
          ctx.arc(x, y, 14 * tw, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = tw;
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.arc(x, y, 2.6, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1;
        }
      });

      ctx.fillStyle = "#ffffff";
      for (const f of snow) {
        f.y += f.s * 0.0035;
        f.drift += 0.004;
        if (f.y > 1.05) {
          f.y = -0.05;
          f.x = Math.random();
        }
        const x = f.x * w + Math.sin(f.drift + t * 0.4) * 18;
        ctx.globalAlpha = f.o;
        ctx.beginPath();
        ctx.arc(x, f.y * h, f.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={ref} data-testid="holiday-canvas" className={className} aria-hidden="true" />;
}
